import { styled, useTheme } from "@mui/material/styles";
import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Button as MuiButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link as RouterLink } from "react-router-dom";
import {
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { format, isValid } from "date-fns";
import {
  CommercialBookDataGrid,
  renderCorporateNumberWithClosedCaption,
  renderIndicatorIconButton,
} from "@/components/DataGrid";
import { ButtonVariantOption, LinkButton } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { getCommercialBookErrorMessage } from "@/utils/commercialBookErrorMessage";
import { useApiClient } from "@/hooks/useApiClient";
import { toast } from "react-toastify";
import {
  DownloadPopupBlockedError,
  downloadInNewTab,
} from "@/utils/downloadInNewTab";
import { downloadStoredCommercialPictureAPI } from "../api/searchStoredCommercialPictures";
import type { StoredCommercialPicture } from "../types";
import {
  OTHER_USER_RESOURCE_LABEL,
  makeOtherUserResourceColumn,
} from "./otherUserResourceColumn";

/** 検索結果0件のときにグリッド内へ表示する文言 */
export const NO_STORED_COMMERCIAL_PICTURE_MESSAGE =
  "該当する取得済商業登記はありません";

interface Props {
  rows: StoredCommercialPicture[];
  loading: boolean;
  /** 共有ONの会社か。ONなら「他ユーザー取得」を表示する */
  isSharable: boolean;
}

/** 検索結果グリッドの1行（登記＝取得依頼明細の単位） */
export interface StoredCommercialPictureDataGridRow {
  id: number;
  commercialPictureRequestId: number;
  requestedAt: string;
  corporateNumber: string;
  closed: boolean;
  companyName: string;
  location: string;
  status: StoredCommercialPicture["status"];
  /** 商業登記列の値: AVAILABLE / "FAILED" / "PROCESSING" */
  uri: string;
  /** エラーメッセージ（取得失敗時のみ。成功・取得中は空文字） */
  errorMessage: string;
  isOtherUserResource: boolean;
}

const StyledDataGrid = styled(CommercialBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

/**
 * APIレスポンス（登記単位）をグリッドの行へ変換する。
 * 商業登記列の値の決め方は取得状況画面（commercialBookStatus）と同じ
 * @param pictures 検索結果
 * @returns グリッドの行
 */
export const convertToStoredCommercialPictureRows = (
  pictures: StoredCommercialPicture[]
): StoredCommercialPictureDataGridRow[] =>
  pictures.map((picture) => ({
    id: picture.id,
    commercialPictureRequestId: picture.commercialPictureRequestId,
    requestedAt: picture.requestedAt,
    corporateNumber: picture.corporateNumber ?? "",
    closed: picture.closed,
    companyName: picture.companyName ?? "",
    location: picture.location ?? "",
    status: picture.status,
    uri:
      picture.status === "SUCCEEDED"
        ? picture.hasFile
          ? "AVAILABLE"
          : ""
        : picture.status === "FAILED"
        ? "FAILED"
        : "PROCESSING",
    errorMessage: getCommercialBookErrorMessage(picture),
    isOtherUserResource: picture.isOtherUserResource,
  }));

/**
 * 検索結果グリッドの列定義（取得状況画面の登記単位の表示に準じ、所属する取得依頼への導線を加える）
 * @param isSharable 共有ONの会社か。ONなら「他ユーザー取得」の列を加える
 * @returns 列定義
 */
export const makeStoredCommercialPictureColumns = (
  isSharable = false
): GridColDef[] => [
  {
    field: "requestedAt",
    headerName: "取得日（依頼日時）",
    width: 180,
    sortable: false,
    align: "center",
    headerAlign: "center",
    renderCell: ({ value }: GridRenderCellParams<string>) => {
      if (value === undefined) return "";
      return format(new Date(value), "yyyy/MM/dd HH:mm");
    },
  },
  {
    field: "corporateNumber",
    headerName: "会社法人等番号",
    width: 200,
    sortable: false,
    renderCell: renderCorporateNumberWithClosedCaption,
  },
  {
    field: "companyName",
    headerName: "商号・名称",
    width: 320,
    sortable: false,
  },
  {
    field: "location",
    headerName: "所在",
    width: 360,
    sortable: false,
  },
  {
    field: "uri",
    headerName: "商業登記",
    width: 100,
    sortable: false,
    headerAlign: "center",
    align: "center",
    renderCell: (
      params: GridRenderCellParams<string, StoredCommercialPictureDataGridRow>
    ) =>
      params.value === "AVAILABLE" ? (
        <StoredPictureDownloadButton detailId={params.row.id} />
      ) : (
        renderIndicatorIconButton(params)
      ),
  },
  {
    field: "errorMessage",
    headerName: "エラーメッセージ",
    minWidth: 240,
    flex: 1,
    sortable: false,
  },
  ...(isSharable ? [makeOtherUserResourceColumn()] : []),
  {
    field: "action",
    headerName: "Action",
    minWidth: 130,
    sortable: false,
    align: "center",
    headerAlign: "center",
    renderCell: ({
      row,
    }: GridRenderCellParams<string, StoredCommercialPictureDataGridRow>) => (
      <LinkButton
        variant={ButtonVariantOption.Outlined}
        size="small"
        wrapperSize={SizingWrapperStyle.INHERIT}
        href={`/commercialbookstatus/${row.commercialPictureRequestId}/?stored=1`}
      >
        取得依頼の詳細
      </LinkButton>
    ),
  },
];

const NoRowsOverlay: React.FC = () => (
  <Stack height="100%" alignItems="center" justifyContent="center">
    <Typography>{NO_STORED_COMMERCIAL_PICTURE_MESSAGE}</Typography>
  </Stack>
);

/**
 * 登記の取得状況ラベル・チップ色。
 * ここは「登記（1社1登記）単位」の個別statusなので、成功/取得中/取得結果を確認中/失敗 の個別文言を使う。
 * 検索フィルタ「取得状況」は取得依頼（親・集約）の一覧に合わせて 依頼中/失敗あり と表記するため、
 * 同一画面で文言が一部異なるが、これは粒度の違いによる意図的な差異。
 */
const STORED_STATUS_LABELS: Record<string, string> = {
  SUCCEEDED: "成功",
  REQUESTING: "取得中",
  NEEDS_REVIEW: "取得結果を確認中",
  FAILED: "失敗",
};
const STORED_STATUS_COLORS: Record<
  string,
  "success" | "info" | "warning" | "error"
> = {
  SUCCEEDED: "success",
  REQUESTING: "info",
  NEEDS_REVIEW: "warning",
  FAILED: "error",
};

/** カード内の1項目（ラベル＋値） */
const CardRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 1.5,
      py: 0.25,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: "right", minWidth: 0, wordBreak: "break-all" }}>
      {children}
    </Box>
  </Box>
);

/**
 * SP（狭幅）向けの登記カード一覧。テーブルは列が多く横スクロールになるため、
 * 1登記=1カードで縦に並べる。
 */
const MobileStoredCards: React.FC<{
  rows: StoredCommercialPictureDataGridRow[];
  loading: boolean;
  isSharable: boolean;
}> = ({ rows, loading, isSharable }) => {
  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }
  if (rows.length === 0) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <Typography>{NO_STORED_COMMERCIAL_PICTURE_MESSAGE}</Typography>
      </Stack>
    );
  }
  return (
    <>
      <Stack spacing={1.5}>
        {rows.map((row) => {
          const isDownloadable = row.uri === "AVAILABLE";
          return (
            <Card key={row.id} variant="outlined">
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {row.companyName || "（商号・名称なし）"}
                  </Typography>
                  <Chip
                    label={STORED_STATUS_LABELS[row.status] ?? row.status}
                    size="small"
                    color={STORED_STATUS_COLORS[row.status] ?? "default"}
                    sx={{ flexShrink: 0 }}
                  />
                </Box>
                <CardRow label="会社法人等番号">
                  <Typography variant="body2">
                    {row.corporateNumber || "—"}
                    {row.closed ? "（閉鎖）" : ""}
                  </Typography>
                </CardRow>
                <CardRow label="本店所在地">
                  <Typography variant="body2">{row.location || "—"}</Typography>
                </CardRow>
                <CardRow label="取得日">
                  <Typography variant="body2">
                    {isValid(new Date(row.requestedAt))
                      ? format(new Date(row.requestedAt), "yyyy/MM/dd HH:mm")
                      : ""}
                  </Typography>
                </CardRow>
                {isSharable && row.isOtherUserResource && (
                  <CardRow label="取得ユーザー">
                    <Typography variant="body2">
                      {OTHER_USER_RESOURCE_LABEL}
                    </Typography>
                  </CardRow>
                )}
                {row.errorMessage ? (
                  <CardRow label="エラーメッセージ">
                    <Typography variant="body2" color="error">
                      {row.errorMessage}
                    </Typography>
                  </CardRow>
                ) : null}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  {isDownloadable ? (
                    <StoredPictureDownloadButton detailId={row.id} />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      商業登記なし
                    </Typography>
                  )}
                  <MuiButton
                    component={RouterLink}
                    to={`/commercialbookstatus/${row.commercialPictureRequestId}/?stored=1`}
                    variant="outlined"
                    size="small"
                    endIcon={<ChevronRightIcon />}
                    aria-label="この登記が含まれる取得依頼の詳細を見る"
                  >
                    詳細
                  </MuiButton>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </>
  );
};

/**
 * 取得済商業登記の検索結果グリッド（登記単位）
 */
export const StoredCommercialPictureFeedGrid: React.FC<Props> = (props) => {
  const { rows, loading, isSharable } = props;
  const gridRows = convertToStoredCommercialPictureRows(rows);
  const columns = makeStoredCommercialPictureColumns(isSharable);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (isMobile) {
    return (
      <MobileStoredCards
        rows={gridRows}
        loading={loading}
        isSharable={isSharable}
      />
    );
  }

  return (
    <StyledDataGrid
      pagination={false}
      hideFooterPagination
      stickyHeader={true}
      autoHeight
      disableColumnMenu
      disableSelectionOnClick={true}
      rows={gridRows}
      columns={columns}
      loading={loading}
      components={{ NoRowsOverlay }}
      sx={{ "--DataGrid-overlayHeight": "120px" }}
    />
  );
};

export const StoredPictureDownloadButton: React.FC<{ detailId: number }> = ({
  detailId,
}) => {
  const { apiClient } = useApiClient();
  const [downloading, setDownloading] = useState(false);
  const download = async (): Promise<void> => {
    setDownloading(true);
    try {
      // BE が Content-Disposition: attachment の署名URLを発行するため、新しいタブから保存される。
      await downloadInNewTab(
        async () =>
          await downloadStoredCommercialPictureAPI(apiClient, detailId)
      );
    } catch (error) {
      toast.error(
        error instanceof DownloadPopupBlockedError
          ? "ポップアップがブロックされました。許可してから再試行してください。"
          : "商業登記のダウンロードに失敗しました。"
      );
    } finally {
      setDownloading(false);
    }
  };
  return (
    <Tooltip title="商業登記をダウンロード">
      <span>
        <IconButton
          disabled={downloading}
          onClick={() => {
            void download();
          }}
          size="small"
          color="primary"
          aria-label="商業登記をダウンロード"
        >
          {downloading ? (
            <CircularProgress size={18} />
          ) : (
            <FileDownloadOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};
